<template>
  <v-card class="data-table-card">
    <!-- 📊 Header con controles -->
    <v-card-title class="d-flex justify-space-between align-center">
      <div class="d-flex align-center gap-2">
        <v-icon color="primary">mdi-table</v-icon>
        <span class="text-h6">{{ title }}</span>
        <v-chip v-if="loading" color="primary" variant="outlined" size="small">
          <v-progress-circular indeterminate size="16" class="mr-1"></v-progress-circular>
          Cargando
        </v-chip>
      </div>
      
      <div class="d-flex gap-2">
        <!-- 🔍 Búsqueda -->
        <v-text-field
          v-model="search"
          placeholder="Buscar..."
          prepend-inner-icon="mdi-magnify"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          style="width: 250px;"
        />
        
        <!-- 📊 Filtros -->
        <v-btn
          icon="mdi-filter-variant"
          variant="outlined"
          @click="showFilters = !showFilters"
          :color="hasActiveFilters ? 'primary' : 'default'"
        />
        
        <!-- 📥 Exportar -->
        <v-menu>
          <template #activator="{ props }">
            <v-btn 
              icon="mdi-download" 
              variant="outlined" 
              v-bind="props"
              :disabled="loading"
            />
          </template>
          <v-list>
            <v-list-item @click="exportData('csv')">
              <v-list-item-title>Exportar CSV</v-list-item-title>
            </v-list-item>
            <v-list-item @click="exportData('excel')">
              <v-list-item-title>Exportar Excel</v-list-item-title>
            </v-list-item>
            <v-list-item @click="exportData('pdf')">
              <v-list-item-title>Exportar PDF</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        
        <!-- ⚙️ Configuración -->
        <v-btn
          icon="mdi-cog"
          variant="outlined"
          @click="showSettings = !showSettings"
        />
      </div>
    </v-card-title>

    <!-- 🔍 Panel de filtros expandible -->
    <v-expand-transition>
      <v-card-text v-show="showFilters" class="pt-0">
        <v-divider class="mb-4"></v-divider>
        <v-row>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.metric"
              :items="availableMetrics"
              label="Métrica"
              variant="outlined"
              density="compact"
              clearable
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-select
              v-model="filters.trend"
              :items="trendOptions"
              label="Tendencia"
              variant="outlined"
              density="compact"
              clearable
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.minValue"
              label="Valor mínimo"
              type="number"
              variant="outlined"
              density="compact"
            />
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field
              v-model="filters.maxValue"
              label="Valor máximo"
              type="number"
              variant="outlined"
              density="compact"
            />
          </v-col>
        </v-row>
        <div class="d-flex justify-end gap-2">
          <v-btn variant="outlined" @click="clearFilters">
            Limpiar filtros
          </v-btn>
          <v-btn color="primary" @click="applyFilters">
            Aplicar filtros
          </v-btn>
        </div>
      </v-card-text>
    </v-expand-transition>

    <!-- 📊 Tabla de datos -->
    <v-data-table
      v-model:items-per-page="itemsPerPage"
      v-model:page="currentPage"
      v-model:sort-by="sortBy"
      :headers="computedHeaders"
      :items="filteredData"
      :loading="loading"
      :search="search"
      class="elevation-0"
      loading-text="Cargando datos..."
      no-data-text="No hay datos disponibles"
      :items-per-page-options="[10, 25, 50, 100]"
      show-current-page
    >
      <!-- 📅 Columna de fecha -->
      <template #item.date="{ item }">
        <div class="d-flex align-center">
          <v-icon size="small" class="mr-2 text-grey">mdi-calendar</v-icon>
          {{ formatDate(item.date) }}
        </div>
      </template>

      <!-- 📊 Columna de métrica -->
      <template #item.metric="{ item }">
        <v-chip
          :color="getMetricColor(item.metric)"
          variant="tonal"
          size="small"
        >
          {{ item.metric }}
        </v-chip>
      </template>

      <!-- 🔢 Columna de valor -->
      <template #item.value="{ item }">
        <div class="text-h6 font-weight-medium">
          {{ formatValue(item.value, item.format) }}
        </div>
      </template>

      <!-- 📈 Columna de tendencia -->
      <template #item.trend="{ item }">
        <div class="d-flex align-center">
          <v-icon 
            :color="getTrendColor(item.trend)" 
            size="small" 
            class="mr-1"
          >
            {{ getTrendIcon(item.trend) }}
          </v-icon>
          <span :class="`text-${getTrendColor(item.trend)}`">
            {{ item.change > 0 ? '+' : '' }}{{ item.change }}
          </span>
        </div>
      </template>

      <!-- 📊 Columna de porcentaje de cambio -->
      <template #item.changePercentage="{ item }">
        <v-chip
          :color="getChangeColor(item.changePercentage)"
          variant="outlined"
          size="small"
        >
          {{ item.changePercentage > 0 ? '+' : '' }}{{ item.changePercentage.toFixed(1) }}%
        </v-chip>
      </template>

      <!-- 🔧 Acciones -->
      <template #item.actions="{ item }">
        <div class="d-flex gap-1">
          <v-btn
            icon="mdi-eye"
            size="small"
            variant="text"
            @click="viewDetails(item)"
          />
          <v-btn
            icon="mdi-chart-line"
            size="small"
            variant="text"
            @click="showChart(item)"
          />
          <v-btn
            icon="mdi-share-variant"
            size="small"
            variant="text"
            @click="shareData(item)"
          />
        </div>
      </template>
    </v-data-table>

    <!-- ⚙️ Configuración del panel -->
    <v-navigation-drawer
      v-model="showSettings"
      location="right"
      temporary
      width="300"
    >
      <v-card-title>
        <v-icon class="mr-2">mdi-cog</v-icon>
        Configuración de tabla
      </v-card-title>
      
      <v-divider></v-divider>
      
      <v-card-text>
        <v-switch
          v-model="settings.showDensity"
          label="Vista compacta"
          color="primary"
        />
        
        <v-switch
          v-model="settings.showActions"
          label="Mostrar acciones"
          color="primary"
        />
        
        <v-switch
          v-model="settings.enableSelection"
          label="Selección múltiple"
          color="primary"
        />
        
        <v-select
          v-model="settings.defaultSort"
          :items="sortOptions"
          label="Ordenar por defecto"
          variant="outlined"
          density="compact"
        />
        
        <v-slider
          v-model="settings.refreshInterval"
          :min="5000"
          :max="300000"
          :step="5000"
          label="Intervalo de actualización (ms)"
          thumb-label
        />
      </v-card-text>
    </v-navigation-drawer>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import type { AnalyticsTableRow } from '@/types/analytics'

// 📝 Props
interface Props {
  title?: string
  data: AnalyticsTableRow[]
  loading?: boolean
  headers?: Array<{
    title: string
    key: string
    sortable?: boolean
    width?: string
  }>
}

const props = withDefaults(defineProps<Props>(), {
  title: 'Datos de Analytics',
  loading: false,
  headers: () => [
    { title: 'Fecha', key: 'date', sortable: true },
    { title: 'Métrica', key: 'metric', sortable: true },
    { title: 'Valor', key: 'value', sortable: true },
    { title: 'Tendencia', key: 'trend', sortable: true },
    { title: 'Cambio %', key: 'changePercentage', sortable: true },
    { title: 'Acciones', key: 'actions', sortable: false, width: '120px' }
  ]
})

// 📤 Emits
const emit = defineEmits<{
  export: [format: string, data: AnalyticsTableRow[]]
  viewDetails: [item: AnalyticsTableRow]
  showChart: [item: AnalyticsTableRow]
  share: [item: AnalyticsTableRow]
}>()

// 🔄 State
const search = ref('')
const showFilters = ref(false)
const showSettings = ref(false)
const currentPage = ref(1)
const itemsPerPage = ref(25)
const sortBy = ref([{ key: 'date', order: 'desc' }])

// 🔍 Filtros
const filters = ref({
  metric: null as string | null,
  trend: null as string | null,
  minValue: null as number | null,
  maxValue: null as number | null
})

// ⚙️ Configuración
const settings = ref({
  showDensity: false,
  showActions: true,
  enableSelection: false,
  defaultSort: 'date',
  refreshInterval: 30000
})

// 📊 Computed
const computedHeaders = computed(() => {
  return props.headers.filter(header => 
    header.key !== 'actions' || settings.value.showActions
  )
})

const filteredData = computed(() => {
  let result = [...props.data]
  
  // Aplicar filtros
  if (filters.value.metric) {
    result = result.filter(item => item.metric === filters.value.metric)
  }
  
  if (filters.value.trend) {
    result = result.filter(item => item.trend === filters.value.trend)
  }
  
  if (filters.value.minValue !== null) {
    result = result.filter(item => item.value >= filters.value.minValue!)
  }
  
  if (filters.value.maxValue !== null) {
    result = result.filter(item => item.value <= filters.value.maxValue!)
  }
  
  return result
})

const hasActiveFilters = computed(() => {
  return Object.values(filters.value).some(value => value !== null)
})

const availableMetrics = computed(() => {
  const metrics = [...new Set(props.data.map(item => item.metric))]
  return metrics.map(metric => ({ title: metric, value: metric }))
})

const trendOptions = [
  { title: 'Subida', value: 'up' },
  { title: 'Bajada', value: 'down' },
  { title: 'Estable', value: 'stable' }
]

const sortOptions = [
  { title: 'Fecha', value: 'date' },
  { title: 'Métrica', value: 'metric' },
  { title: 'Valor', value: 'value' },
  { title: 'Cambio %', value: 'changePercentage' }
]

// 🔧 Methods
const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('es-DO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const formatValue = (value: number, format?: string) => {
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('es-DO', {
        style: 'currency',
        currency: 'DOP'
      }).format(value)
    case 'percentage':
      return `${value}%`
    case 'time':
      const hours = Math.floor(value / 60)
      const minutes = value % 60
      return `${hours}h ${minutes}m`
    default:
      return new Intl.NumberFormat('es-DO').format(value)
  }
}

const getMetricColor = (metric: string) => {
  const colors: Record<string, string> = {
    'Usuarios Activos': 'blue',
    'Ingresos': 'green',
    'Conversiones': 'orange',
    'Tiempo de Sesión': 'purple'
  }
  return colors[metric] || 'grey'
}

const getTrendIcon = (trend: string) => {
  const icons: Record<string, string> = {
    'up': 'mdi-trending-up',
    'down': 'mdi-trending-down',
    'stable': 'mdi-trending-neutral'
  }
  return icons[trend] || 'mdi-minus'
}

const getTrendColor = (trend: string) => {
  const colors: Record<string, string> = {
    'up': 'success',
    'down': 'error',
    'stable': 'warning'
  }
  return colors[trend] || 'grey'
}

const getChangeColor = (changePercentage: number) => {
  if (changePercentage > 0) return 'success'
  if (changePercentage < 0) return 'error'
  return 'warning'
}

const clearFilters = () => {
  filters.value = {
    metric: null,
    trend: null,
    minValue: null,
    maxValue: null
  }
}

const applyFilters = () => {
  // Los filtros se aplican automáticamente vía computed
  showFilters.value = false
}

const exportData = (format: string) => {
  emit('export', format, filteredData.value)
}

const viewDetails = (item: AnalyticsTableRow) => {
  emit('viewDetails', item)
}

const showChart = (item: AnalyticsTableRow) => {
  emit('showChart', item)
}

const shareData = (item: AnalyticsTableRow) => {
  emit('share', item)
}

// 🎯 Lifecycle
onMounted(() => {
  // Cargar configuración guardada
  const savedSettings = localStorage.getItem('dataTableSettings')
  if (savedSettings) {
    settings.value = { ...settings.value, ...JSON.parse(savedSettings) }
  }
})

// 💾 Guardar configuración
watch(settings, (newSettings) => {
  localStorage.setItem('dataTableSettings', JSON.stringify(newSettings))
}, { deep: true })
</script>

<style scoped>
.data-table-card {
  border-radius: 16px;
  overflow: hidden;
}

.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}

/* Personalización de la tabla */
:deep(.v-data-table) {
  border-radius: 0 !important;
}

:deep(.v-data-table-header) {
  background-color: rgba(var(--v-theme-surface-variant), 0.5);
}

:deep(.v-data-table__tr:hover) {
  background-color: rgba(var(--v-theme-primary), 0.04) !important;
}

/* Animaciones suaves */
.v-chip {
  transition: all 0.2s ease;
}

.v-btn {
  transition: all 0.2s ease;
}
</style>
